import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface UseInterviewerTabsProps {
  aiInterviewerId: string;
  insightsEnabled?: boolean;
}

interface UseInterviewerTabsReturn {
  activeTab: number;
  handleChangeTab: (index: number) => void;
}

export const TAB_INDICES = {
  CANDIDATES: 0,
  GUIDE: 1,
  SETTINGS: 2,
  NOTIFICATIONS: 3,
  TEST_INTERVIEW: 4,
  INSIGHTS: 5,
} as const;

export const pickTabIndex = (pathname: string): number => {
  if (pathname.includes("/notifications")) {
    return TAB_INDICES.NOTIFICATIONS;
  }
  if (pathname.includes("/candidates")) {
    return TAB_INDICES.CANDIDATES;
  }
  if (pathname.includes("/guide")) {
    return TAB_INDICES.GUIDE;
  }
  if (pathname.includes("/settings")) {
    return TAB_INDICES.SETTINGS;
  }
  if (pathname.includes("/test-interview")) {
    return TAB_INDICES.TEST_INTERVIEW;
  }
  if (pathname.includes("/insights")) {
    return TAB_INDICES.INSIGHTS;
  }
  // Default to candidates tab
  return TAB_INDICES.CANDIDATES;
};

const useInterviewerTabs = ({
  aiInterviewerId,
  insightsEnabled,
}: UseInterviewerTabsProps): UseInterviewerTabsReturn => {
  const location = useLocation();
  const navigate = useNavigate();

  let activeTab = pickTabIndex(location.pathname);
  if (!insightsEnabled && activeTab === TAB_INDICES.INSIGHTS) {
    activeTab = TAB_INDICES.CANDIDATES;
  }

  const handleChangeTab = useCallback(
    (index: number): void => {
      if (!aiInterviewerId) return;

      // Candidates (0), Guide (1), Settings (2), Notifications (3), Test interviews (4)
      let tabPath = "candidates";
      if (index === TAB_INDICES.GUIDE) {
        tabPath = "guide";
      } else if (index === TAB_INDICES.SETTINGS) {
        tabPath = "settings";
      } else if (index === TAB_INDICES.NOTIFICATIONS) {
        tabPath = "notifications";
      } else if (index === TAB_INDICES.TEST_INTERVIEW) {
        tabPath = "test-interview";
      } else if (index === TAB_INDICES.INSIGHTS) {
        tabPath = "insights";
      }
      navigate(
        {
          pathname: `/ai-interviewer/${aiInterviewerId}/${tabPath}`,
          search: location.search,
        },
        { replace: true }
      );
    },
    [aiInterviewerId, navigate, location.search]
  );

  return {
    activeTab,
    handleChangeTab,
  };
};

export default useInterviewerTabs;
